import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Save, Plus, PlusCircle, Loader2, AlertCircle, Image as ImageIcon, CheckCircle, X, Play, Music, Lock } from 'lucide-react';
import { rawFetch } from '../services/api';
import { buildTenantPath } from '../hooks/useTenant';

interface CoverUploadProgress {
  status: 'idle' | 'presigning' | 'uploading' | 'saving' | 'complete' | 'error';
  progress: number;
  error: string | null;
}

// ═══════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════

interface LyricLine {
  start: number;
  duration: number;
  holdUntil: number;
  text: string;
}

interface LyricLineDraft {
  id: string;
  start: string;
  duration: string;
  holdUntil: string;
  text: string;
}

interface RowErrors {
  start?: string;
  duration?: string;
  holdUntil?: string;
  text?: string;
}

interface TrackData {
  id: string;
  trackReference: string;
  title: string;
  coverArtUrl: string | null;
  streamUrl: string | null;
  verificationStatus: string;
  lyrics: LyricLine[] | string | null;
  songLength: number | null;
  previewStart: number | null;
  previewLength: number | null;
  enableLyrics: boolean;
  isPublished?: boolean;
}

interface TrackEditorPageProps {
  slug: string;
  trackRef: string;
}

// ═══════════════════════════════════════════════════════════
// VALIDATION
// ═══════════════════════════════════════════════════════════

function parseNum(raw: string): number | null {
  const v = Number(raw);
  return Number.isFinite(v) && v >= 0 ? v : null;
}

function validateLyrics(rows: LyricLineDraft[]): { isValid: boolean; errors: Record<string, RowErrors> } {
  const errors: Record<string, RowErrors> = {};

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const rowErr: RowErrors = {};
    const start = parseNum(row.start);
    const duration = parseNum(row.duration);
    const holdUntil = parseNum(row.holdUntil);

    // Basic field validation
    if (start === null) {
      rowErr.start = 'Must be a valid number ≥ 0';
    }
    if (duration === null) {
      rowErr.duration = 'Must be a valid number ≥ 0';
    }
    if (holdUntil === null) {
      rowErr.holdUntil = 'Must be a valid number ≥ 0';
    }
    if (!row.text.trim()) {
      rowErr.text = 'Text is required';
    }

    // Relationship validation (only if all fields parse)
    if (start !== null && duration !== null && holdUntil !== null) {
      // holdUntil must be > start
      if (holdUntil <= start) {
        rowErr.holdUntil = 'Must be greater than start';
      }

      // duration <= (holdUntil - start)
      if (duration > (holdUntil - start)) {
        rowErr.duration = 'Must be less than or equal to (holdUntil - start)';
      }

      // holdUntil <= next row start (can be equal)
      if (i < rows.length - 1) {
        const nextStart = parseNum(rows[i + 1].start);
        if (nextStart !== null && holdUntil > nextStart) {
          rowErr.holdUntil = 'Must be less than or equal to next row start';
        }
      }

      // start >= previous row holdUntil (can be equal)
      if (i > 0) {
        const prevHoldUntil = parseNum(rows[i - 1].holdUntil);
        if (prevHoldUntil !== null && start < prevHoldUntil) {
          rowErr.start = 'Must be greater than or equal to previous row holdUntil';
        }
      }
    }

    if (Object.keys(rowErr).length > 0) {
      errors[row.id] = rowErr;
    }
  }

  return { isValid: Object.keys(errors).length === 0, errors };
}

function draftToLyrics(rows: LyricLineDraft[]): LyricLine[] {
  return rows.map((r) => ({
    start: Number(r.start),
    duration: Number(r.duration),
    holdUntil: Number(r.holdUntil),
    text: r.text,
  }));
}

function lyricsToDraft(lyrics: LyricLine[]): LyricLineDraft[] {
  return lyrics.map((l: any) => ({
    id: crypto.randomUUID(),
    start: String(l.start ?? l.startTime ?? ''),
    duration: String(l.duration ?? l.typeDuration ?? ''),
    holdUntil: String(l.holdUntil ?? ''),
    text: l.text ?? '',
  }));
}

// ═══════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════

export default function TrackEditorPage({ slug, trackRef }: TrackEditorPageProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [track, setTrack] = useState<TrackData | null>(null);

  // Draft state
  const [title, setTitle] = useState('');
  const [lyricsDraft, setLyricsDraft] = useState<LyricLineDraft[]>([]);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [newCoverS3Uri, setNewCoverS3Uri] = useState<string | null>(null);

  // Preview settings
  const [previewStart, setPreviewStart] = useState<string>('');
  const [previewLength, setPreviewLength] = useState<string>('');
  const [enableLyrics, setEnableLyrics] = useState(false);
  const [songLength, setSongLength] = useState<number | null>(null);

  // Validation
  const [validationErrors, setValidationErrors] = useState<Record<string, RowErrors>>({});

  // Cover upload progress
  const [coverUploadProgress, setCoverUploadProgress] = useState<CoverUploadProgress>({
    status: 'idle',
    progress: 0,
    error: null,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load track data
  useEffect(() => {
    async function fetchTrack() {
      try {
        const res = await rawFetch(`/api/public/tracks/ref/${trackRef}`);
        if (!res.ok) {
          setError('Track not found');
          return;
        }
        const json = await res.json() as { data: TrackData };
        const data = json.data;

        // Must be verified with lyrics
        if (data.verificationStatus !== 'VERIFIED') {
          setError('Only verified tracks can be edited');
          return;
        }

        // Block editing published/minted tracks
        if (data.isPublished) {
          setError('This track has been published in a pressing and is permanently locked. It cannot be edited.');
          return;
        }

        setTrack(data);
        setTitle(data.title);

        // Preview settings
        setPreviewStart(data.previewStart != null ? String(data.previewStart) : '');
        setPreviewLength(data.previewLength != null ? String(data.previewLength) : '');
        setEnableLyrics(!!data.enableLyrics);
        setSongLength(data.songLength ?? null);

        // Handle case where lyrics might be a JSON string instead of parsed array
        let parsedLyrics: any = data.lyrics;
        if (typeof parsedLyrics === 'string') {
          try {
            parsedLyrics = JSON.parse(parsedLyrics);
          } catch {
            parsedLyrics = [];
          }
        }
        if (Array.isArray(parsedLyrics) && parsedLyrics.length > 0) {
          setLyricsDraft(lyricsToDraft(parsedLyrics));
        }

        setCoverPreview(data.coverArtUrl);
      } catch {
        setError('Failed to load track');
      } finally {
        setLoading(false);
      }
    }

    fetchTrack();
  }, [trackRef]);

  // Validate on every lyrics change
  useEffect(() => {
    if (lyricsDraft.length > 0) {
      const { errors } = validateLyrics(lyricsDraft);
      setValidationErrors(errors);
    }
  }, [lyricsDraft]);

  // Update a single row field
  const updateRow = useCallback((id: string, field: keyof LyricLineDraft, value: string) => {
    setLyricsDraft((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    );
  }, []);

  // Insert row at a specific index
  const insertRowAt = useCallback((index: number) => {
    const newRow: LyricLineDraft = {
      id: crypto.randomUUID(),
      start: '',
      duration: '',
      holdUntil: '',
      text: '',
    };
    setLyricsDraft((prev) => {
      const copy = [...prev];
      copy.splice(index, 0, newRow);
      return copy;
    });
  }, []);

  // Append row at end
  const appendRow = useCallback(() => {
    const newRow: LyricLineDraft = {
      id: crypto.randomUUID(),
      start: '',
      duration: '',
      holdUntil: '',
      text: '',
    };
    setLyricsDraft((prev) => [...prev, newRow]);
  }, []);

  // Delete row
  const deleteRow = useCallback((id: string) => {
    setLyricsDraft((prev) => prev.filter((r) => r.id !== id));
  }, []);

  // Cover art file selection
  const handleCoverChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      alert('Cover art must be JPEG, PNG, or WebP');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      alert('Cover art must be under 20MB');
      return;
    }

    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
    setNewCoverS3Uri(null); // Reset since we have a new file
  }, []);

  // Upload new cover art with progress tracking
  const uploadCover = async (): Promise<string | null> => {
    if (!coverFile || !track) return null;

    // Presign
    setCoverUploadProgress({ status: 'presigning', progress: 10, error: null });

    const presignRes = await rawFetch(
      `/api/public/tenants/tracks/${track.id}/cover/presign`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug },
        body: JSON.stringify({
          contentType: coverFile.type,
          fileSize: coverFile.size,
        }),
      }
    );

    if (!presignRes.ok) {
      setCoverUploadProgress({ status: 'error', progress: 0, error: 'Failed to get upload URL' });
      throw new Error('Failed to get upload URL');
    }

    const { uploadUrl, coverS3Uri } = await presignRes.json() as { uploadUrl: string; coverS3Uri: string };

    // Upload to R2 with XHR for progress
    setCoverUploadProgress({ status: 'uploading', progress: 15, error: null });

    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const pct = Math.round(15 + (event.loaded / event.total) * 65);
          setCoverUploadProgress((prev) => ({ ...prev, progress: Math.min(pct, 80) }));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Upload failed with status ${xhr.status}`));
      };
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', coverFile.type);
      xhr.send(coverFile);
    });

    return coverS3Uri;
  };

  // Save all changes
  const handleSave = async () => {
    if (!track) return;

    // Validate lyrics only if we have any
    if (lyricsDraft.length > 0) {
      const { isValid, errors } = validateLyrics(lyricsDraft);
      setValidationErrors(errors);
      if (!isValid) return;
    }

    // Validate title
    if (!title.trim()) {
      alert('Title cannot be empty');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Upload cover if changed
      let coverUri = newCoverS3Uri;
      if (coverFile && !coverUri) {
        coverUri = await uploadCover();
        setNewCoverS3Uri(coverUri);
      }

      // Build payload
      setCoverUploadProgress((prev) =>
        prev.status !== 'idle' ? { ...prev, status: 'saving', progress: 85 } : prev
      );

      const payload: Record<string, any> = {
        title: title.trim(),
        previewStart: previewStart !== '' ? Number(previewStart) : undefined,
        previewLength: previewLength !== '' ? Number(previewLength) : undefined,
        enableLyrics,
      };
      if (lyricsDraft.length > 0) {
        payload.lyrics = draftToLyrics(lyricsDraft);
      }
      if (coverUri) {
        payload.coverS3Uri = coverUri;
      }

      // Save
      const res = await rawFetch(
        `/api/public/tenants/tracks/${track.id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const errJson = await res.json() as { error: string };
        throw new Error(errJson.error || 'Save failed');
      }

      // Show complete state briefly if we uploaded
      if (coverUploadProgress.status !== 'idle') {
        setCoverUploadProgress({ status: 'complete', progress: 100, error: null });
        await new Promise((r) => setTimeout(r, 1500));
      }

      // Navigate back to catalog
      window.location.href = buildTenantPath(slug, 'catalog');
    } catch (err: any) {
      setError(err.message || 'Failed to save changes');
      setCoverUploadProgress({ status: 'idle', progress: 0, error: null });
    } finally {
      setSaving(false);
    }
  };

  const hasLyrics = lyricsDraft.length > 0;
  const { isValid } = hasLyrics ? validateLyrics(lyricsDraft) : { isValid: true };
  const canSave = isValid && title.trim().length > 0 && !saving;

  // ─── LOADING / ERROR STATES ───

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-electric-cyan animate-spin" />
      </div>
    );
  }

  if (error && !track) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center px-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Cannot Edit Track</h1>
          <p className="text-text-secondary">{error}</p>
          <button
            onClick={() => { window.location.href = buildTenantPath(slug, 'catalog'); }}
            className="mt-6 px-4 py-2 rounded-lg bg-white/10 text-white text-sm hover:bg-white/20 transition-colors"
          >
            Back to Catalog
          </button>
        </div>
      </div>
    );
  }

  // ─── MAIN EDITOR ───

  const isCoverUploading = coverUploadProgress.status !== 'idle';

  return (
    <div className="min-h-screen bg-deep-charcoal">
      {/* Cover Upload Progress Modal */}
      <AnimatePresence>
        {isCoverUploading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          >
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="relative w-full max-w-sm bg-[#1A1A1A] border border-white/10 rounded-xl shadow-2xl overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
                <h2 className="text-lg font-semibold text-white">
                  {coverUploadProgress.status === 'error' ? 'Upload Error' : 'Uploading Cover Art'}
                </h2>
                {(coverUploadProgress.status === 'error' || coverUploadProgress.status === 'complete') && (
                  <button
                    onClick={() => setCoverUploadProgress({ status: 'idle', progress: 0, error: null })}
                    className="p-1.5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
                  >
                    <X className="w-5 h-5 text-text-secondary" />
                  </button>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                {coverUploadProgress.error ? (
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-300">{coverUploadProgress.error}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Image preview */}
                    {coverPreview && (
                      <div className="mx-auto w-24 h-24 rounded-lg overflow-hidden border border-white/10">
                        <img src={coverPreview} alt="Cover preview" className="w-full h-full object-cover" />
                      </div>
                    )}

                    {/* Progress bar */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-text-secondary">
                          {coverUploadProgress.status === 'presigning' && 'Preparing upload...'}
                          {coverUploadProgress.status === 'uploading' && 'Uploading cover art...'}
                          {coverUploadProgress.status === 'saving' && 'Saving changes...'}
                          {coverUploadProgress.status === 'complete' && 'Saved!'}
                        </span>
                        <span className="text-sm font-mono text-electric-cyan">
                          {coverUploadProgress.progress}%
                        </span>
                      </div>

                      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                        <motion.div
                          className={`h-full rounded-full ${
                            coverUploadProgress.status === 'complete' ? 'bg-green-500' : 'bg-electric-cyan'
                          }`}
                          initial={{ width: 0 }}
                          animate={{ width: `${coverUploadProgress.progress}%` }}
                          transition={{ duration: 0.3, ease: 'easeOut' }}
                        />
                      </div>

                      {coverUploadProgress.status === 'complete' && (
                        <motion.div
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex items-center gap-2 text-sm text-green-400"
                        >
                          <CheckCircle className="w-4 h-4" />
                          <span>Cover art updated successfully</span>
                        </motion.div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-white/5 rounded-none">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <button
            onClick={() => { window.location.href = buildTenantPath(slug, 'catalog'); }}
            className="flex items-center gap-2 text-text-secondary hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Catalog</span>
          </button>

          <span className="terminal-label text-xs">track_editor::active</span>

          <button
            onClick={handleSave}
            disabled={!canSave}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-electric-cyan text-black text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-electric-cyan/90 transition-colors"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            <span>Save</span>
          </button>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-20 pb-12 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-center gap-2"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </motion.div>
        )}

        {/* Track Metadata Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="glass-card p-6 rounded-xl mb-6"
        >
          <h2 className="terminal-label text-xs mb-4">Track Details</h2>

          <div className="flex flex-col sm:flex-row gap-6">
            {/* Cover Art */}
            <div className="flex-shrink-0">
              <div
                onClick={() => fileInputRef.current?.click()}
                className="w-32 h-32 rounded-lg overflow-hidden bg-surface-1 border border-white/10 cursor-pointer hover:border-electric-cyan/50 transition-colors relative group"
              >
                {coverPreview ? (
                  <img src={coverPreview} alt="Cover art" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-text-muted" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="text-xs text-white font-medium">Change</span>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleCoverChange}
                className="hidden"
              />
              <p className="text-[10px] text-text-muted mt-1 text-center">Click to change</p>
            </div>

            {/* Title */}
            <div className="flex-1">
              <label className="block text-xs text-text-muted mb-1.5 font-medium">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-white border border-gray-300 text-black placeholder-gray-400 text-sm focus:outline-none focus:border-electric-cyan/50 transition-colors"
                placeholder="Track title"
              />
            </div>
          </div>
        </motion.div>

        {/* Preview Settings Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.05 }}
          className="glass-card p-6 rounded-xl mb-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <Play className="w-4 h-4 text-electric-cyan" />
            <h2 className="terminal-label text-xs">Preview Settings</h2>
          </div>
          <p className="text-xs text-text-muted mb-4">Configure the audio preview clip played on capture pages and public player.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            <div>
              <label className="block text-xs text-text-muted mb-1.5 font-medium">
                Preview Start (seconds)
              </label>
              <input
                type="number"
                min="0"
                max={songLength ? songLength : undefined}
                step="1"
                value={previewStart}
                onChange={(e) => setPreviewStart(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-white border border-gray-300 text-black placeholder-gray-400 text-sm focus:outline-none focus:border-electric-cyan/50 transition-colors"
                placeholder="30"
              />
              {songLength && (
                <p className="text-[10px] text-text-muted mt-1">Track length: {Math.round(songLength)}s</p>
              )}
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1.5 font-medium">
                Preview Length (seconds)
              </label>
              <input
                type="number"
                min="1"
                max={songLength ? songLength : undefined}
                step="1"
                value={previewLength}
                onChange={(e) => setPreviewLength(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-white border border-gray-300 text-black placeholder-gray-400 text-sm focus:outline-none focus:border-electric-cyan/50 transition-colors"
                placeholder="30"
              />
            </div>
          </div>

          {/* Enable Lyrics Toggle */}
          <div className="flex items-center justify-between py-3 border-t border-white/10">
            <div className="flex items-center gap-2">
              <Music className="w-4 h-4 text-text-muted" />
              <div>
                <p className="text-sm text-white font-medium">Enable Lyrics</p>
                <p className="text-xs text-text-muted">Display synced lyrics during playback</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setEnableLyrics(!enableLyrics)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                enableLyrics ? 'bg-electric-cyan' : 'bg-white/20'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  enableLyrics ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </motion.div>

        {/* Lyrics Editor Card */}
        {hasLyrics && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="glass-card p-6 rounded-xl"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="terminal-label text-xs">Lyrics Timeline</h2>
            <button
              onClick={appendRow}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-electric-cyan/10 text-electric-cyan border border-electric-cyan/30 text-xs font-medium hover:bg-electric-cyan/20 transition-colors cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Row
            </button>
          </div>

          {/* Validation summary */}
          {Object.keys(validationErrors).length > 0 && (
            <div className="mb-4 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs flex items-center gap-2">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              {Object.keys(validationErrors).length} row(s) have validation errors
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-text-muted text-xs font-medium pb-2 pr-2 w-8">#</th>
                  <th className="text-left text-text-muted text-xs font-medium pb-2 px-2 w-[8em] min-w-[8em]">Start (s)</th>
                  <th className="text-left text-text-muted text-xs font-medium pb-2 px-2 w-[6em] min-w-[6em]">Duration (s)</th>
                  <th className="text-left text-text-muted text-xs font-medium pb-2 px-2 w-[8em] min-w-[8em]">HoldUntil (s)</th>
                  <th className="text-left text-text-muted text-xs font-medium pb-2 px-2 w-auto">Text</th>
                  <th className="text-left text-text-muted text-xs font-medium pb-2 pl-2 w-20">Actions</th>
                </tr>
              </thead>
              <tbody>
                {lyricsDraft.map((row, index) => {
                  const rowErr = validationErrors[row.id];
                  return (
                    <LyricRow
                      key={row.id}
                      row={row}
                      index={index}
                      errors={rowErr}
                      onUpdate={updateRow}
                      onInsertBelow={() => insertRowAt(index + 1)}
                      onDelete={() => deleteRow(row.id)}
                      totalRows={lyricsDraft.length}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>

          {lyricsDraft.length === 0 && (
            <div className="text-center py-8 text-text-muted text-sm">
              No lyrics rows. Click "Add Row" to start.
            </div>
          )}
        </motion.div>
        )}
      </main>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// LYRIC ROW COMPONENT
// ═══════════════════════════════════════════════════════════

interface LyricRowProps {
  row: LyricLineDraft;
  index: number;
  errors?: RowErrors;
  onUpdate: (id: string, field: keyof LyricLineDraft, value: string) => void;
  onInsertBelow: () => void;
  onDelete: () => void;
  totalRows: number;
}

function LyricRow({ row, index, errors, onUpdate, onInsertBelow, onDelete, totalRows }: LyricRowProps) {
  const hasError = !!errors;

  return (
    <tr className={`border-b border-white/5 ${hasError ? 'bg-red-500/5' : ''}`}>
      <td className="py-2 pr-2 text-text-muted text-xs font-mono">{index + 1}</td>
      <td className="py-2 px-2">
        <input
          type="text"
          value={row.start}
          onChange={(e) => onUpdate(row.id, 'start', e.target.value)}
          className={`w-full px-2 py-1.5 rounded bg-white text-black text-xs font-mono focus:outline-none focus:border-electric-cyan/50 transition-colors ${
            errors?.start ? 'border-2 border-red-500' : 'border border-gray-300'
          }`}
          placeholder="0"
        />
      </td>
      <td className="py-2 px-2">
        <input
          type="text"
          value={row.duration}
          onChange={(e) => onUpdate(row.id, 'duration', e.target.value)}
          className={`w-full px-2 py-1.5 rounded bg-white text-black text-xs font-mono focus:outline-none focus:border-electric-cyan/50 transition-colors ${
            errors?.duration ? 'border-2 border-red-500' : 'border border-gray-300'
          }`}
          placeholder="0"
        />
      </td>
      <td className="py-2 px-2">
        <input
          type="text"
          value={row.holdUntil}
          onChange={(e) => onUpdate(row.id, 'holdUntil', e.target.value)}
          className={`w-full px-2 py-1.5 rounded bg-white text-black text-xs font-mono focus:outline-none focus:border-electric-cyan/50 transition-colors ${
            errors?.holdUntil ? 'border-2 border-red-500' : 'border border-gray-300'
          }`}
          placeholder="0"
        />
      </td>
      <td className="py-2 px-2">
        <input
          type="text"
          value={row.text}
          onChange={(e) => onUpdate(row.id, 'text', e.target.value)}
          className={`w-full px-2 py-1.5 rounded bg-white text-black text-xs focus:outline-none focus:border-electric-cyan/50 transition-colors ${
            errors?.text ? 'border-2 border-red-500' : 'border border-gray-300'
          }`}
          placeholder="Lyric text..."
        />
      </td>
      <td className="py-2 pl-2">
        <div className="flex items-center gap-1">
          <button
            onClick={onInsertBelow}
            className="p-1 rounded hover:bg-electric-cyan/10 text-text-muted hover:text-electric-cyan transition-colors cursor-pointer"
            title="Insert row below"
          >
            <PlusCircle className="w-3.5 h-3.5" />
          </button>
          {totalRows > 1 && (
            <button
              onClick={onDelete}
              className="p-1 rounded hover:bg-red-500/10 text-text-muted hover:text-red-400 transition-colors cursor-pointer"
              title="Delete row"
            >
              <span className="text-xs font-bold">×</span>
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
